import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, MessageCircle, Send, ShoppingBag } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { channels, contact } from "@kanata/shared";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trackWhatsappClicked } from "@/lib/tracking";

const methods: { icon: LucideIcon; label: string; value: string; href: string; note: string }[] = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: contact.whatsappNumber,
    href: contact.whatsappUrl,
    note: "Paling cepat dibalas. Untuk tanya-tanya maupun proyek kantor.",
  },
  {
    icon: Mail,
    label: "Email",
    value: contact.email,
    href: `mailto:${contact.email}`,
    note: "Untuk pertanyaan panjang atau kirim dokumen.",
  },
  {
    icon: ShoppingBag,
    label: "Tokopedia",
    value: "Kanata Indonesia",
    href: channels.tokopedia,
    note: "Kalau kamu lebih suka belanja lewat marketplace.",
  },
];

/**
 * The contact form hands off to WhatsApp rather than posting anywhere.
 *
 * Deliberate: Kanata has no ticketing system, and a form that silently drops
 * a message into an inbox nobody watches is worse than no form. This composes
 * the message and opens the conversation where somebody actually replies.
 */
function ContactForm() {
  const [sent, setSent] = useState(false);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const name = String(data.get("name") ?? "").trim();
    const company = String(data.get("company") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const message = String(data.get("message") ?? "").trim();

    const lines = [
      "Halo Kanata, saya mau tanya.",
      "",
      `Nama: ${name}`,
      company && `Perusahaan: ${company}`,
      email && `Email: ${email}`,
      "",
      message,
    ].filter(Boolean);

    // Opened from script, not a link, so the site-wide listener can't see it.
    trackWhatsappClicked("contact_form");
    window.open(
      `https://wa.me/${contact.whatsappDigits}?text=${encodeURIComponent(lines.join("\n"))}`,
      "_blank",
      "noopener,noreferrer",
    );
    setSent(true);
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border border-ink/8 bg-white p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="name" className="mb-1.5 block">
            Nama <span className="text-destructive">*</span>
          </Label>
          <Input id="name" name="name" required />
        </div>
        <div>
          <Label htmlFor="company" className="mb-1.5 block">
            Perusahaan <span className="font-normal text-ink/45">(opsional)</span>
          </Label>
          <Input id="company" name="company" />
        </div>
      </div>

      <div className="mt-4">
        <Label htmlFor="email" className="mb-1.5 block">
          Email <span className="text-destructive">*</span>
        </Label>
        <Input id="email" name="email" type="email" required />
      </div>

      <div className="mt-4">
        <Label htmlFor="message" className="mb-1.5 block">
          Pesan <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="message"
          name="message"
          required
          rows={5}
          placeholder="Ceritakan yang kamu cari — meja untuk di rumah, proyek kantor, atau jumlah tertentu."
        />
      </div>

      <Button type="submit" size="lg" className="mt-5 w-full sm:w-auto">
        <Send />
        Kirim lewat WhatsApp
      </Button>

      <p className="mt-3 text-xs leading-relaxed text-ink/50">
        {sent
          ? "WhatsApp sudah dibuka di tab baru. Kalau tidak muncul, cek pemblokir pop-up browser kamu."
          : "Tombol ini menyusun pesannya lalu membuka WhatsApp — jadi kamu bisa baca dulu sebelum kirim."}
      </p>
    </form>
  );
}

export default function Contact() {
  return (
    <>
      <Seo
        title="Kontak — Kanata Indonesia"
        description="Hubungi Kanata lewat WhatsApp atau email. Untuk pertanyaan produk maupun proyek kantor."
      />

      <PageHero
        eyebrow="Kontak"
        title="Ngobrol dulu juga boleh."
        lead="Kamu bisa langsung beli sendiri di sini kapan saja. Tapi kalau mau tanya dulu, kami balas."
      />

      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
            <div>
              <ul className="space-y-4">
                {methods.map((m) => (
                  <li key={m.label}>
                    <a
                      href={m.href}
                      data-wa-placement="contact"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex gap-4 rounded-2xl border border-ink/8 bg-white p-5 transition-colors hover:border-ink/20"
                    >
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-light text-brand-dark">
                        <m.icon className="size-4.5" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-ink/45">
                          {m.label}
                        </p>
                        <p className="mt-0.5 font-medium text-ink">{m.value}</p>
                        <p className="mt-1 text-sm leading-relaxed text-ink/60">{m.note}</p>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex gap-4 rounded-2xl border border-ink/8 bg-cream/60 p-5">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-brand">
                  <MapPin className="size-4.5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink/45">
                    Showroom
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-ink/70">{contact.showroom}</p>
                </div>
              </div>

              <p className="mt-6 text-sm leading-relaxed text-ink/55">
                Mau cek status pesanan yang sudah dibuat?{" "}
                <Link to="/cek-pesanan" className="font-medium text-brand underline underline-offset-2">
                  Cek di sini
                </Link>{" "}
                pakai nomor pesanan dan nomor HP kamu — tidak perlu tanya ke kami.
              </p>
            </div>

            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}
